import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatCheckbox, MatCheckboxChange } from '@angular/material/checkbox';
import {
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogTitle
} from '@angular/material/dialog';
import { MatFormField, MatLabel } from '@angular/material/input';
import { MatOption, MatSelect } from '@angular/material/select';
import { IdleRobotEscalation, MessageService, RobotEscalationService, User, UserService } from '@ng-cloud/badger-core';
import { Subscription } from 'rxjs';
import { RobotEscalationIdleAlertService } from '../robot-escalation-idle-alert.service';

@Component({
  selector: 'fleet-robot-escalation-idle-dialog',
  templateUrl: './robot-escalation-idle-dialog.component.html',
  styleUrls: ['./robot-escalation-idle-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    FormsModule,
    MatButton,
    MatCheckbox,
    MatDialogActions,
    MatDialogClose,
    MatDialogContent,
    MatDialogTitle,
    MatFormField,
    MatLabel,
    MatOption,
    MatSelect
  ]
})
export class RobotEscalationIdleDialogComponent implements OnInit, OnDestroy {
  idleEscalations: IdleRobotEscalation[] = [];
  autoPopupDisabled = false;
  idleMinutes: number;

  skyboxUsers: User[] = [];

  selectedAssigneeByEscalationId: { [id: number]: number | null } = {};
  updatingEscalationIds = new Set<number>();

  private readonly subscriptions = new Subscription();

  constructor(
    private idleAlertService: RobotEscalationIdleAlertService,
    private robotEscalationService: RobotEscalationService,
    private userService: UserService,
    private messageService: MessageService
  ) {
  }

  ngOnInit() {
    this.subscriptions.add(
      this.idleAlertService.idleEscalations$.subscribe((items) => {
        // The server owns the NEW determination, based on the last-opened preference which is not
        // written until this dialog closes, so it stays stable across in-dialog refreshes.
        this.idleEscalations = items;
        items.forEach((item) => {
          if (!(item.id in this.selectedAssigneeByEscalationId)) {
            this.selectedAssigneeByEscalationId[item.id] = item.assignedTo;
          }
        });
      })
    );

    this.subscriptions.add(
      this.idleAlertService.autoPopupDisabled$.subscribe((disabled) => {
        this.autoPopupDisabled = disabled;
      })
    );

    this.subscriptions.add(
      this.idleAlertService.idleMinutes$.subscribe((minutes) => {
        this.idleMinutes = minutes;
      })
    );

    this.subscriptions.add(
      this.userService.getUsers({ has_role: 'skybox_level_1' }).subscribe((users) => {
        this.skyboxUsers = users;
      })
    );

    this.idleAlertService.refreshIdleEscalations();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  onAutoPopupChanged(event: MatCheckboxChange) {
    this.idleAlertService.setAutoPopupDisabled(event.checked);
  }

  idleDuration(item: IdleRobotEscalation): string {
    if (!item.idleSince) {
      return '';
    }
    const diffMs = Date.now() - item.idleSince.getTime();
    const min = Math.max(Math.floor(diffMs / 60000), 0);
    if (min < 60) {
      return `${min}m`;
    }
    const hours = Math.floor(min / 60);
    const minutes = min % 60;
    return `${hours}h ${minutes}m`;
  }

  isUpdating(escalationId: number): boolean {
    return this.updatingEscalationIds.has(escalationId);
  }

  onReassignSelectionChanged(escalation: IdleRobotEscalation, assigneeId: number | null) {
    this.selectedAssigneeByEscalationId[escalation.id] = assigneeId;
    this.reassign(escalation);
  }

  reassign(escalation: IdleRobotEscalation) {
    const escalationId = escalation.id;
    if (this.isUpdating(escalationId)) {
      return;
    }

    const priorAssignee = escalation.assignedTo;
    escalation.assignedTo = this.selectedAssigneeByEscalationId[escalationId] || null;
    if (priorAssignee === escalation.assignedTo) {
      return;
    }

    this.updatingEscalationIds.add(escalationId);
    this.robotEscalationService.updateRobotEscalation(escalation).subscribe({
      next: () => {
        this.updatingEscalationIds.delete(escalationId);
        this.removeEscalationFromDialog(escalationId);
        this.idleAlertService.refreshIdleEscalations();
      },
      error: () => {
        this.messageService.alert('Failed to reassign escalation. Please try again.');
        escalation.assignedTo = priorAssignee;
        this.selectedAssigneeByEscalationId[escalationId] = priorAssignee;
        this.updatingEscalationIds.delete(escalationId);
      }
    });
  }

  snooze(escalation: IdleRobotEscalation, minutes: number) {
    const escalationId = escalation.id;
    if (this.isUpdating(escalationId)) {
      return;
    }

    const priorStatus = escalation.status;
    const priorSnoozeUntil = escalation.snoozeUntil;
    escalation.status = 'snoozed';
    escalation.snoozeUntil = new Date(Date.now() + minutes * 60000);

    this.updatingEscalationIds.add(escalationId);
    // Pass current assignee through snooze call so backend keeps assignment context.
    this.robotEscalationService.snoozeRobotEscalation(escalationId, minutes, escalation.assignedTo).subscribe({
      next: () => {
        this.updatingEscalationIds.delete(escalationId);
        // A snoozed escalation leaves the idle set until the snooze expires.
        this.removeEscalationFromDialog(escalationId);
        this.idleAlertService.refreshIdleEscalations();
      },
      error: () => {
        this.messageService.alert('Failed to snooze escalation. Please try again.');
        escalation.status = priorStatus;
        escalation.snoozeUntil = priorSnoozeUntil;
        this.updatingEscalationIds.delete(escalationId);
      }
    });
  }

  private removeEscalationFromDialog(escalationId: number) {
    this.idleEscalations = this.idleEscalations.filter((item) => item.id !== escalationId);
    delete this.selectedAssigneeByEscalationId[escalationId];
  }
}
